/**
 * "What's live" visibility checks (pure + one small fetch helper).
 *
 * The storefront generates a machine-readable surface for every published
 * location: robots.txt (allowing AI crawlers), llms.txt, sitemap.xml, a
 * homepage with Restaurant JSON-LD, and the public menu API. This module
 * fetches those endpoints and turns each into a pass/fail row with a concrete
 * fix hint. It deliberately says nothing about AI recommendations — we only
 * verify that the site is discoverable and machine-readable.
 *
 * CORS is already allowed on these endpoints, so the dashboard can fetch them
 * directly from the storefront origin.
 */

export type VisibilityCheckKey = 'robots' | 'llms' | 'sitemap' | 'homepage' | 'menu';

export interface VisibilityHttpResult {
  ok: boolean;
  status: number;
  text: string;
}

export interface VisibilityTargets {
  robots: VisibilityHttpResult | null;
  llms: VisibilityHttpResult | null;
  sitemap: VisibilityHttpResult | null;
  homepage: VisibilityHttpResult | null;
  menu: VisibilityHttpResult | null;
}

export interface VisibilityCheck {
  key: VisibilityCheckKey;
  label: string;
  status: 'pass' | 'fail';
  detail: string;
  fix: string;
  /** The exact URL that was checked, so the owner can inspect it. */
  url: string;
}

type FetchLike = (url: string) => Promise<VisibilityHttpResult>;

/** AI crawler the robots.txt file must explicitly allow. */
export const REQUIRED_AI_CRAWLER = 'GPTBot';

export function robotsAllowsGptBot(text: string): boolean {
  if (text.trim().length === 0) return false;
  // Match the user-agent line case-insensitively and allow surrounding spaces.
  return /user-agent:\s*GPTBot\b/i.test(text);
}

export function llmsMentionsLocation(text: string, locationName: string): boolean {
  const name = locationName.trim().toLowerCase();
  if (name.length === 0) return false;
  return text.toLowerCase().includes(name);
}

export function sitemapHasUrlset(text: string): boolean {
  return /<urlset[\s>]/i.test(text);
}

export function homepageIsMachineReadable(html: string): boolean {
  const compact = html.replace(/\s+/g, '').toLowerCase();
  return (
    compact.includes('application/ld+json') &&
    compact.includes('"@type":"restaurant"') &&
    compact.includes('hasmenu')
  );
}

function trimBase(baseUrl: string): string {
  return baseUrl.replace(/\/+$/, '');
}

/**
 * Strips characters spreadsheet apps treat as formula prefixes so a pasted
 * "link" can never become a CSV/Excel formula when exported.
 */
export function sanitizeExternalOrderLink(raw: string): string {
  return raw.trim().replace(/^[=+\-@\t\r]+/, '').trim();
}

/** A usable external ordering link must be an absolute http(s) URL. */
export function isValidExternalOrderLink(value: string): boolean {
  if (value.length === 0) return false;
  try {
    const url = new URL(value);
    return url.protocol === 'http:' || url.protocol === 'https:';
  } catch {
    return false;
  }
}

export function visibilityUrls(baseUrl: string): Record<VisibilityCheckKey, string> {
  const base = trimBase(baseUrl);
  return {
    robots: `${base}/robots.txt`,
    llms: `${base}/llms.txt`,
    sitemap: `${base}/sitemap.xml`,
    homepage: `${base}/`,
    menu: `${base}/api/store/menu`,
  };
}

function failed(result: VisibilityHttpResult | null): boolean {
  return !result || !result.ok || result.status >= 400;
}

/**
 * Turns the raw responses into pass/fail rows. `locationName` is compared
 * against llms.txt so we know the file describes this location.
 */
export function evaluateVisibility(
  targets: VisibilityTargets,
  baseUrl: string,
  locationName: string,
): VisibilityCheck[] {
  const urls = visibilityUrls(baseUrl);

  const robotsPass = !failed(targets.robots) && robotsAllowsGptBot(targets.robots?.text ?? '');
  const llmsPass =
    !failed(targets.llms) && llmsMentionsLocation(targets.llms?.text ?? '', locationName);
  const sitemapPass = !failed(targets.sitemap) && sitemapHasUrlset(targets.sitemap?.text ?? '');
  const homepagePass = !failed(targets.homepage) && homepageIsMachineReadable(targets.homepage?.text ?? '');
  const menuPass = !failed(targets.menu);

  return [
    {
      key: 'robots',
      label: 'AI crawlers allowed (robots.txt)',
      status: robotsPass ? 'pass' : 'fail',
      detail: robotsPass
        ? `robots.txt names ${REQUIRED_AI_CRAWLER}.`
        : `robots.txt is missing${targets.robots ? ` (HTTP ${targets.robots.status})` : ' or unreachable'} or does not allow ${REQUIRED_AI_CRAWLER}.`,
      fix: 'Publish this location, then reload. The storefront writes robots.txt automatically.',
      url: urls.robots,
    },
    {
      key: 'llms',
      label: 'llms.txt names this location',
      status: llmsPass ? 'pass' : 'fail',
      detail: llmsPass
        ? 'llms.txt is live and mentions this location.'
        : 'llms.txt is missing, or it does not mention this location by name.',
      fix: 'Publish this location so the storefront generates llms.txt with its name and menu.',
      url: urls.llms,
    },
    {
      key: 'sitemap',
      label: 'XML sitemap lists the site (sitemap.xml)',
      status: sitemapPass ? 'pass' : 'fail',
      detail: sitemapPass ? 'sitemap.xml contains a <urlset>.' : 'sitemap.xml is missing or malformed.',
      fix: 'Publish this location so the storefront serves sitemap.xml.',
      url: urls.sitemap,
    },
    {
      key: 'homepage',
      label: 'Homepage exposes Restaurant structured data',
      status: homepagePass ? 'pass' : 'fail',
      detail: homepagePass
        ? 'The homepage includes Restaurant JSON-LD with a menu.'
        : 'The homepage is missing application/ld+json, Restaurant schema, or hasMenu.',
      fix: 'Publish this location and add a menu. The storefront adds schema.org markup automatically.',
      url: urls.homepage,
    },
    {
      key: 'menu',
      label: 'Public menu API responds',
      status: menuPass ? 'pass' : 'fail',
      detail: menuPass ? 'The storefront menu API returned 200.' : 'The storefront menu API did not return 200.',
      fix: 'Add at least one available menu item, then publish this location.',
      url: urls.menu,
    },
  ];
}

const defaultFetch: FetchLike = async (url) => {
  const response = await fetch(url, { headers: { accept: '*/*' } });
  return { ok: response.ok, status: response.status, text: await response.text() };
};

async function fetchTarget(fetcher: FetchLike, url: string): Promise<VisibilityHttpResult | null> {
  try {
    return await fetcher(url);
  } catch {
    // Network/CORS failures are a fail with a fix hint, not a crash.
    return null;
  }
}

/** Fetches every visibility endpoint. Never throws. */
export async function fetchVisibilityTargets(
  baseUrl: string,
  fetcher: FetchLike = defaultFetch,
): Promise<VisibilityTargets> {
  const urls = visibilityUrls(baseUrl);
  const [robots, llms, sitemap, homepage, menu] = await Promise.all([
    fetchTarget(fetcher, urls.robots),
    fetchTarget(fetcher, urls.llms),
    fetchTarget(fetcher, urls.sitemap),
    fetchTarget(fetcher, urls.homepage),
    fetchTarget(fetcher, urls.menu),
  ]);
  return { robots, llms, sitemap, homepage, menu };
}

/** Fetch + evaluate in one call, for the Visibility page. */
export async function runVisibilityChecks(
  baseUrl: string,
  locationName: string,
  fetcher: FetchLike = defaultFetch,
): Promise<VisibilityCheck[]> {
  const targets = await fetchVisibilityTargets(baseUrl, fetcher);
  return evaluateVisibility(targets, baseUrl, locationName);
}
